import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type ToastTone = 'neutral' | 'success' | 'attention' | 'critical';

export interface ToastMessage {
  id: string;
  title: string;
  body?: string;
  tone?: ToastTone;
}

interface ToastContextValue {
  pushToast: (message: Omit<ToastMessage, 'id'>) => string;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONE: Record<ToastTone, { border: string; background: string }> = {
  neutral: { border: 'var(--border)', background: 'var(--surface-raised)' },
  success: { border: 'var(--sev-healthy)', background: 'var(--sev-healthy-bg)' },
  attention: { border: 'var(--sev-attention)', background: 'var(--sev-attention-bg)' },
  critical: { border: 'var(--sev-critical)', background: 'var(--sev-critical-bg)' },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const pushToast = useCallback((message: Omit<ToastMessage, 'id'>) => {
    const id = `toast-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts((current) => [...current, { id, tone: 'neutral', ...message }]);
    window.setTimeout(() => dismissToast(id), 6000);
    return id;
  }, [dismissToast]);

  const value = useMemo(() => ({ pushToast, dismissToast }), [dismissToast, pushToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-live="polite"
        aria-relevant="additions text"
        style={{
          position: 'fixed',
          top: 64,
          right: 24,
          zIndex: 120,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          width: 320,
          pointerEvents: 'none',
        }}
      >
        {toasts.map((toast) => {
          const colors = TONE[toast.tone ?? 'neutral'];
          return (
            <div
              key={toast.id}
              style={{
                pointerEvents: 'auto',
                padding: '12px 14px',
                border: `1px solid ${colors.border}`,
                borderRadius: 'var(--r-md, 10px)',
                background: colors.background,
                color: 'var(--text, #1B1D22)',
                boxShadow: 'var(--elev-2, 0 2px 4px rgba(17,19,24,.06))',
              }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{toast.title}</div>
                  {toast.body && <div className="v-caption" style={{ marginTop: 4 }}>{toast.body}</div>}
                </div>
                <button type="button" aria-label="Dismiss notification" onClick={() => dismissToast(toast.id)} className="btn-reset" style={{ cursor: 'pointer', minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)' }}>
                  ×
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
}
